import { endpoint } from "@/api/endpoint.js";
import { api } from "@/api/request.js";
import DataTable from "@/components/DataTable/DataTable.jsx";
import { userRole } from "@/constant/userRole.js";
import { useDeleteDeviceParameterMutation, useGetAllDeviceParameterQuery, useUpdateDeviceParameterMutation } from "@/redux/api/deviceParameterApi";
import { flare } from "@flareapp/js";
import { Button, Input, Modal, Spin, Switch, Tag, Tooltip } from "antd";
import axios from "axios";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { FaPlus } from "react-icons/fa";
import { RiDeleteBin5Line } from "react-icons/ri";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";

const DeviceParameters = ({ hasTariff, device }) => {
  // hooks
  const userDetails = useSelector((state) => state.user?.userDetails || {});

  // states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [singleDeviceParameter, setSingleDeviceParameter] = useState({});
  const [parameters, setParameters] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUnitModalOpen, setIsUnitModalOpen] = useState(false);
  const [unit, setUnit] = useState(false);

  // rkt query
  const {
    data: deviceParameters,
    isLoading,
    isFetching,
    refetch: getDeviceParameters,
  } = useGetAllDeviceParameterQuery({ device: device?._id, limit: pageSize, page: currentPage, type: "formula" }, { skip: !device?._id });

  const [deleteDeviceParameterMutation, { isLoading: isDeleting }] = useDeleteDeviceParameterMutation();
  const [updateDeviceParameterMutation, { isLoading: isUpdating }] = useUpdateDeviceParameterMutation();

  // handlers
  const handleUnitCancel = () => {
    setIsUnitModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const getParameters = async () => {
    const response = await axios.get(`/system-parameter`, {
      withCredentials: true,
      params: { page: 1, sort: "-1" },
    });
    if (response) {
      setParameters(response.data.results);
    }
  };

  const deleteDeviceParameter = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to delete this parameter?",
      //icon: "warning",
      dangerMode: true,
      showCancelButton: true,
      confirmButtonText: "Confirm",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteDeviceParameterMutation(id).unwrap();
          Swal.fire({
            title: "Done!",
            text: "Parameter deleted successfully",
            icon: "success",
            timer: 2000,
            confirmButtonText: "OK",
          });
        } catch (error) {
          flare.report(error);
          Swal.fire({
            text: "Failed to delete parameter",
            icon: "error",
            timer: 2000,
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  const updateDeviceParameter = async ({ id, body }) => {
    try {
      await updateDeviceParameterMutation({ id, body }).unwrap();
      Swal.fire({
        title: "Done!",
        text: "Parameter updated successfully",
        icon: "success",
        timer: 2000,
        confirmButtonText: "OK",
      });
    } catch (error) {
      flare.report(error);
      Swal.fire({
        text: "Failed to update parameter",
        icon: "error",
        timer: 2000,
        confirmButtonText: "OK",
      });
    }
  };

  const onAssingSystemParaToDevicePara = async () => {
    try {
      const { message } = await api.post(endpoint.assing_system_parameter, {
        systemParameterId: singleDeviceParameter?.systemParameterId,
        deviceParameterId: singleDeviceParameter?._id,
        tariffParameterId: singleDeviceParameter?.tariffParameterId,
      });

      Swal.fire({
        title: "Done!",
        text: message,
        icon: "success",
        timer: 2000,
        confirmButtonText: "OK",
      });
      setIsModalOpen(false);
      getDeviceParameters();
    } catch {
      Swal.fire({
        // title: "Done!",
        text: Error?.response?.data?.message,
        icon: "error",
        timer: 2000,
        confirmButtonText: "OK",
      });
    }
  };

  useEffect(() => {
    getParameters();
  }, []);

  const columns = [
    {
      title: "No.",
      render: (_, item, index) => <div>{(currentPage - 1) * pageSize + index + 1}</div>,
      width: "60px",
      align: "center",
      show: true,
    },
    {
      title: "Device Parameter Name",
      render: (row) => <div className="">{row?.name}</div>,
      align: "center",
      show: true,
    },
    {
      title: "System Parameter Name",
      render: (row) => (
        <div className="text-capitalize">
          {row?.systemParameter?.name || ""} {row?.systemParameter?.value ? `(${row?.systemParameter?.value})` : ""}
          {!row?.systemParameter?.name && !row?.systemParameter?.value ? "--" : ""}
        </div>
      ),
      align: "center",
      show: true,
    },
    {
      title: "Tariff Parameter Name",
      render: (row) => (
        <div className="text-capitalize">
          {row?.tariffParameter?.name || ""} {row?.tariffParameter?.value ? `(${row?.tariffParameter?.value})` : ""}
          {!row?.tariffParameter?.name && !row?.tariffParameter?.value ? "--" : ""}
        </div>
      ),
      align: "center",
      show: hasTariff,
    },

    {
      title: (
        <Tooltip title={`Apply device multiplier (x${device?.multiply || 1}) to this parameter`}>
          <span>Multiplier</span>
        </Tooltip>
      ),
      render: (row) => {
        const isEnabled = Boolean(row?.isMultiply);
        const multiplier = device?.multiply || 1;

        return (
          <div className="d-flex align-items-center justify-content-center gap-2">
            <Switch
              size="small"
              checked={isEnabled}
              disabled={userDetails.role === userRole.public}
              onChange={(checked) =>
                updateDeviceParameter({
                  id: row?._id,
                  body: { isMultiply: checked },
                })
              }
            />
            <Tooltip title={isEnabled ? `Multiplying by ${multiplier}` : "Using raw value (no multiplier)"}>
              <Tag
                color={isEnabled ? "blue" : "default"}
                className="m-0 cursor-pointer"
                style={{
                  fontSize: "11px",
                  borderRadius: "10px",
                  padding: "0 6px",
                  fontWeight: 500,
                  cursor: userDetails.role === userRole.public ? "not-allowed" : "pointer",
                }}
                onClick={() => {
                  if (userDetails.role !== userRole.public) {
                    updateDeviceParameter({
                      id: row?._id,
                      body: { isMultiply: !isEnabled },
                    });
                  }
                }}
              >
                {isEnabled ? `×${multiplier}` : "Off"}
              </Tag>
            </Tooltip>
          </div>
        );
      },
      align: "center",
      show: true,
    },
    {
      title: "Action",
      render: (row) => (
        <div>
          <button
            disabled={userDetails.role === userRole.public}
            className="btn btn-edit me-1"
            title="Assign System Parameter"
            onClick={() => {
              setIsModalOpen(true);
              setSingleDeviceParameter({
                ...row,
                systemParameterId: row?.systemParameter?._id || "",
                systemParameterUnit: row?.systemParameter?.value || "",
                tariffParameterId: row?.tariffParameter?._id || "",
                tariffParameterUnit: row?.tariffParameter?.value || "",
              });
            }}
          >
            <FaPlus />
            <span className="fw-bold fs-5">S</span>
          </button>

          <button
            disabled={userDetails.role === userRole.public}
            className="btn btn-view-gray me-1"
            title="Assign Device Parameter Unit"
            onClick={() => {
              setIsUnitModalOpen(row?._id);
              setUnit(row?.unit || null);
            }}
          >
            <FaPlus />
            <span className="fw-bold fs-5">D</span>
          </button>
          <button
            disabled={userDetails.role === userRole.public}
            className="btn me-1"
            style={{ background: "red" }}
            title="Delete Device Parameter"
            onClick={() => {
              deleteDeviceParameter(row?._id);
            }}
          >
            <RiDeleteBin5Line size={16} />
          </button>
        </div>
      ),
      align: "center",
      show: true,
    },
  ].filter((item) => item?.show);

  const loading = isLoading || isFetching || isDeleting || isUpdating;

  return (
    <div>
      <h5 className="mb-3">Available Device Parameters</h5>

      <Modal title={`Update Device Parameter Unit`} open={isUnitModalOpen} onCancel={handleUnitCancel} footer={null}>
        <Input size="large" value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="Enter Parameter Unit" />

        <div className="text-right mt-3">
          <Button
            htmlType="submit"
            type="primary"
            className="me-2"
            onClick={() => {
              updateDeviceParameter({ id: isUnitModalOpen, body: { unit } });
              setIsUnitModalOpen(false);
            }}
          >
            Update
          </Button>
          <Button htmlType="button" className="bg-secondary text-white" onClick={handleUnitCancel}>
            Cancel
          </Button>
        </div>
      </Modal>
      <Modal
        title={`Assign System Parameter Name - ${singleDeviceParameter?.name}`}
        open={isModalOpen}
        onCancel={handleCancel}
        // cancelButtonProps={{ className: "btn btn-secondary h-auto" }}
        // onOk={onAssingSystemParaToDevicePara}
        // okButtonProps={{
        //   className: "btn btn-success h-auto",
        //   loading,
        // }}
        footer={null}
      >
        <div className="d-flex">
          <select
            className="form-select me-2"
            value={singleDeviceParameter?.systemParameterId}
            onChange={(e) => {
              setSingleDeviceParameter({
                ...singleDeviceParameter,
                systemParameterId: e.target.value,
                systemParameterUnit: parameters.find((item) => item._id === e.target.value)?.value || "",
              });
            }}
          >
            <option value="">Select system parameter</option>
            {parameters
              .filter((item) => item?.name !== "Electricity Usage" && item?.name !== "Maximum Demand")
              .map(({ _id, name }) => (
                <option value={_id} key={_id}>
                  {name}
                </option>
              ))}
          </select>
          <Input size="large" value={singleDeviceParameter?.systemParameterUnit || ""} disabled style={{ width: "fit-content" }} />
        </div>
        {hasTariff && (
          <>
            <h2
              style={{
                color: "rgba(0, 0, 0, 0.88)",
                fontWeight: "600",
                fontSize: "16px",
                marginTop: "20px",
              }}
            >
              Assign Tariff Settings
            </h2>
            <div className="d-flex">
              <select
                className="form-select me-2"
                value={singleDeviceParameter?.tariffParameterId}
                onChange={(e) => {
                  setSingleDeviceParameter({
                    ...singleDeviceParameter,
                    tariffParameterId: e.target.value,
                    tariffParameterUnit: parameters.find((item) => item._id === e.target.value)?.value || "",
                  });
                }}
              >
                <option value="">Select Tariff Parameter</option>
                {parameters
                  .filter((item) => item?.name === "Electricity Usage" || item?.name === "Maximum Demand")
                  .map(({ _id, name }) => (
                    <option value={_id} key={_id}>
                      {name}
                    </option>
                  ))}
              </select>
              <Input size="large" value={singleDeviceParameter?.tariffParameterUnit || ""} disabled style={{ width: "fit-content" }} />
            </div>
          </>
        )}

        <div className="text-right mt-3">
          <Button htmlType="submit" type="primary" className="me-2" onClick={onAssingSystemParaToDevicePara}>
            Update
          </Button>
          <Button htmlType="button" type="primary" className="bg-secondary" onClick={handleCancel}>
            Cancel
          </Button>
        </div>
      </Modal>
      <Spin spinning={loading}>
        <DataTable
          columns={columns}
          dataSource={deviceParameters?.data || []}
          pagination={{
            pageSize: pageSize,
            current: currentPage,
            total: deviceParameters?.meta?.total ?? 0,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
            pageSizeOptions: [10, 20, 50],
            responsive: true,
          }}
          onChange={({ pageSize: newPageSize, current: newCurrentPage }) => {
            setPageSize(newPageSize);
            setCurrentPage(newCurrentPage);
          }}
        />
      </Spin>
    </div>
  );
};

export default DeviceParameters;

DeviceParameters.propTypes = {
  hasTariff: PropTypes.bool,
  device: PropTypes.object,
};
